import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { SKIP_GLOBAL_LOADING } from './skip-global-loading.context';

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private totalRequests = 0;

  /**
   * On the apply-fund wizard, skip NgxSpinner only for step-1 portal calls
   * (draft load/submit, agency letter). Step 2+ uses the global overlay again.
   */
  private static readonly applyFundStepOneApiFragments = [
    '/FundBorrower/GetFirstStep',
    'FundCompany/GetCompanyTypes',
    'FundCompany/GetCompanyActivities',
    '/FundBorrower/SaveDraftFirstStep',
    '/FundBorrower/SaveDraftSecondStep',
    '/FundBorrower/SaveDraftFundingDetails',
    '/FundBorrower/CreateInitialFund',
  ] as const;

  /** Nafath request + status polling use inline UI; no global overlay. */
  private static readonly nafathApiFragments = [
    '/nafath/request',
    '/checknafath',
  ] as const;

  constructor(
    private readonly loadingService: NgxSpinnerService,
    private readonly router: Router
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.shouldSkipGlobalLoading(request)) {
      return next.handle(request);
    }

    this.totalRequests++;
    this.loadingService.show();
    return next.handle(request).pipe(
      finalize(() => {
        this.totalRequests--;
        if (this.totalRequests === 0) {
          this.loadingService.hide();
        }
      })
    );
  }

  /** Per-request opt-out, Nafath polling, or apply-fund step 1 only (local / inline loaders). */
  private shouldSkipGlobalLoading(request: HttpRequest<unknown>): boolean {
    if (request.context.get(SKIP_GLOBAL_LOADING)) {
      return true;
    }
    if (this.isNafathApi(request.url)) {
      return true;
    }
    const routeUrl = this.router.url || '';
    if (!/FundApplication\/Apply(\/|$|\?|#)/i.test(routeUrl)) {
      return false;
    }
    return this.isApplyFundStepOneApi(request.url);
  }

  private isApplyFundStepOneApi(requestUrl: string): boolean {
    const u = requestUrl.toLowerCase();
    return LoadingInterceptor.applyFundStepOneApiFragments.some((frag) =>
      u.includes(frag.toLowerCase())
    );
  }

  private isNafathApi(requestUrl: string): boolean {
    const u = requestUrl.toLowerCase();
    return LoadingInterceptor.nafathApiFragments.some((frag) =>
      u.includes(frag.toLowerCase())
    );
  }
}
